import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authStore } from '../../api/client';
import { listMyOrgs } from '../../api/orgs';
import {
  copyBlueprint, createBlueprint, deleteBlueprintDraft, getBlueprint, getBlueprintVersion, listLibraryBlueprints,
  listOrgBlueprints, publishBlueprint,
} from '../../api/blueprints';
import EmptyState from '../ui/EmptyState';
import { useToast } from '../ui/Toast';
import BlueprintCard from './BlueprintCard';
import BlueprintEditor from './BlueprintEditor';

const ORG_KEY = 'samastix.blueprints.orgId';

/**
 * Interview Blueprint Library: "Samastix Library" (global, read-only for orgs)
 * and "My Organization" (org-owned drafts + published versions).
 */
export default function BlueprintLibrary({ scope }) {
  const toast = useToast();
  const navigate = useNavigate();
  const isSuper = authStore.role === 'SUPER_ADMIN';
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false });

  useEffect(() => {
    listMyOrgs().then((res) => {
      const list = (Array.isArray(res.data) ? res.data : []).filter((o) => o.myRole || !isSuper);
      setOrgs(list);
      let remembered = null;
      try { remembered = localStorage.getItem(ORG_KEY); } catch { /* storage unavailable */ }
      const initial = list.find((o) => o.id === remembered) || list[0];
      if (initial) setOrgId(initial.id);
    }).catch(() => setOrgs([]));
  }, [isSuper]);

  const selectedOrg = orgs.find((o) => o.id === orgId);
  const canManageOrg = isSuper || ['OWNER', 'ADMIN'].includes(selectedOrg?.myRole);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      if (scope === 'library') {
        const res = await listLibraryBlueprints();
        setRows(res.data || []);
      } else if (orgId) {
        const res = await listOrgBlueprints(orgId);
        setRows(res.data || []);
      } else {
        setRows([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load blueprints');
    } finally {
      setLoading(false);
    }
  }, [scope, orgId, toast]);

  useEffect(() => { reload(); }, [reload]);

  const preview = async (b) => {
    try {
      if (!b.currentVersionId) return edit(b);
      const res = await getBlueprintVersion(b.currentVersionId);
      setEditor({ open: true, mode: 'preview', version: res.data });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    }
  };

  const edit = async (b) => {
    try {
      const res = await getBlueprint(b.id);
      setEditor({ open: true, mode: 'edit', detail: res.data });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not open draft');
    }
  };

  const copy = async (b) => {
    if (!orgId) { toast.error('Join or select an organization first'); return; }
    try {
      const res = await copyBlueprint(b.id, orgId);
      toast.success('Copied to your organization as a draft');
      setEditor({ open: true, mode: 'edit', detail: res.data });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Copy failed');
    }
  };

  const publish = async (b) => {
    try {
      const res = await publishBlueprint(b.id);
      toast.success(`Published v${res.data.versionNo}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Publish failed');
    }
  };

  const remove = async (b) => {
    if (!window.confirm('Delete this unpublished draft?')) return;
    try {
      await deleteBlueprintDraft(b.id);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const createNew = async () => {
    try {
      const res = await createBlueprint({ orgId: scope === 'library' ? null : orgId, name: 'New blueprint', roleFamily: 'Software Engineering', draft: { items: [] } });
      setEditor({ open: true, mode: 'edit', detail: res.data });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Create failed');
    }
  };

  const use = (b) => navigate(`/job-openings?blueprintVersionId=${b.currentVersionId}`);
  const btn = 'text-xs px-2.5 py-1 border border-slate-200 rounded-lg hover:bg-slate-50';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <p className="text-sm text-ink-muted max-w-2xl">
          {scope === 'library'
            ? 'Samastix blueprints define what a role is assessed on: topics, difficulty, weights and evidence. Use one on a job, or copy it to adapt for your organization.'
            : 'Your organization\'s blueprints. Edit the draft, then publish: jobs pin a specific version and never change silently.'}
        </p>
        <div className="flex items-center gap-2">
          {(scope === 'org' || !isSuper) && orgs.length > 1 && (
            <select className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" value={orgId || ''}
              onChange={(e) => { setOrgId(e.target.value); try { localStorage.setItem(ORG_KEY, e.target.value); } catch { /* ignore */ } }}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          {((scope === 'library' && isSuper) || (scope === 'org' && canManageOrg && orgId)) && (
            <button type="button" className="btn btn-sm btn-primary" onClick={createNew}>+ New blueprint</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card py-12 text-sm text-slate-500 text-center rounded-xl">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState title={scope === 'library' ? 'No library blueprints yet' : 'No organization blueprints yet'}
          subtitle={scope === 'library' ? 'A Samastix admin publishes library blueprints.' : 'Copy one from the Samastix Library to get started.'} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((b) => (
            <BlueprintCard key={b.id} blueprint={b} actions={(
              <>
                <button type="button" className={btn} onClick={() => preview(b)}>Preview</button>
                {b.currentVersionId && <button type="button" className={btn} onClick={() => use(b)}>Use</button>}
                {scope === 'library' && b.currentVersionId && canManageOrg && <button type="button" className={btn} onClick={() => copy(b)}>Copy</button>}
                {((scope === 'org' && canManageOrg) || (scope === 'library' && isSuper)) && (
                  <>
                    <button type="button" className={btn} onClick={() => edit(b)}>Edit draft</button>
                    <button type="button" className={btn} onClick={() => publish(b)}>Publish</button>
                  </>
                )}
                {scope === 'org' && canManageOrg && b.currentVersionId && <button type="button" className={btn} onClick={() => copy(b)}>Duplicate</button>}
                {scope === 'org' && canManageOrg && !b.currentVersionId && <button type="button" className={`${btn} text-red-600`} onClick={() => remove(b)}>Delete</button>}
              </>
            )} />
          ))}
        </div>
      )}

      <BlueprintEditor
        open={editor.open}
        mode={editor.mode}
        detail={editor.detail}
        version={editor.version}
        orgId={scope === 'library' ? null : orgId}
        onClose={() => setEditor({ open: false })}
        onSaved={() => { setEditor({ open: false }); reload(); }}
      />
    </div>
  );
}
